/**
 * LibraryManagerModal — Trinity-facing management surface for the
 * Executive View, launched from the analyst AI tab header.
 *
 * Deliberately NOT in the Admin area (busy, pending its own redesign)
 * and NOT in the Executive View (that surface carries no conditional
 * admin chrome). Two tabs over a shared model-instance picker:
 *
 *   Prompts          — the executive prompt library for the instance:
 *                      edit / enable / reorder / delete, plus a
 *                      binding-health check (do the frozen entity IDs
 *                      still resolve against the instance?).
 *   Executive access — brand enablement + subheader text. Scenario
 *                      visibility is NOT configured here: executives
 *                      see all AI-enabled (tagged) scenarios of an
 *                      enabled instance (read-only list shown).
 *   What-If          — curates the Output/Input candidate sets exposed
 *                      to the What-If Control Bar dropdowns
 *                      (IsExecutiveVisible, main API) and authors the
 *                      example question library shown on the What-If
 *                      HOME state (ExecutivePrompt rows, Agentic-API).
 *                      See EXEC_WHATIF_v1_design.md §10.
 *   Tornado          — the executive tornado library (ExecutivePrompt
 *                      rows, tool='executive_tornado'): the same
 *                      edit / enable / reorder / delete / health
 *                      management as Prompts (the tab reuses PromptsTab
 *                      with a tool filter). New analyses are published
 *                      from a generated Tornado report in the Reports
 *                      tab, not from here.
 *
 * Instance list comes from the same scope-options endpoint the scope
 * panel uses (v2-AI-enabled instances for the client).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';

import PopupDialog from 'src/components/shared/PopupDialog';
import {
  PROMPT_TITLE_BOLD_HELP,
  renderPromptTitle,
  stripPromptMarkdown,
} from 'src/components/shared/PromptTitle';

import { listScopeOptions, ScopeOptionsInstance } from '../api';

import {
  deleteLibraryPrompt,
  ExecutiveConfig,
  getExecutiveConfig,
  LibraryHealthResult,
  LibraryPrompt,
  libraryHealthCheck,
  listLibraryPrompts,
  patchLibraryPrompt,
  putExecutiveConfig,
  TORNADO_TOOL,
  TornadoPromptParams,
} from './api';
import WhatIfTab from './WhatIfTab';

interface LibraryManagerModalProps {
  open: boolean;
  onClose: () => void;
  clientId: number | null;
  sessionId: string | null;
}

const LibraryManagerModal: React.FC<LibraryManagerModalProps> = ({
  open,
  onClose,
  clientId,
  sessionId,
}) => {
  const [instances, setInstances] = useState<ScopeOptionsInstance[]>([]);
  const [instancesError, setInstancesError] = useState<string | null>(null);
  const [instanceId, setInstanceId] = useState<number | ''>('');
  const [tab, setTab] = useState<'prompts' | 'config' | 'whatif' | 'tornado'>(
    'prompts'
  );

  useEffect(() => {
    if (!open || clientId == null || sessionId == null) return undefined;
    let cancelled = false;
    setInstancesError(null);
    listScopeOptions(clientId, sessionId)
      .then((resp) => {
        if (cancelled) return;
        setInstances(resp.instances ?? []);
        if (resp.instances?.length === 1) {
          setInstanceId(resp.instances[0].model_instance_id);
        }
      })
      .catch(() => {
        if (!cancelled) setInstancesError('Failed to load model instances.');
      });
    return () => {
      cancelled = true;
    };
  }, [open, clientId, sessionId]);

  return (
    <PopupDialog
      open={open}
      close={onClose}
      title="Executive View Management"
      maxWidth="lg"
      hideDialogActions
    >
      <Stack spacing={2} sx={{ minHeight: '55vh' }}>
        {instancesError && <Alert severity="error">{instancesError}</Alert>}

        <FormControl size="small" sx={{ maxWidth: 480 }}>
          <InputLabel id="exec-mgmt-instance-label">Model instance</InputLabel>
          <Select
            labelId="exec-mgmt-instance-label"
            label="Model instance"
            value={instanceId}
            onChange={(e) => setInstanceId(e.target.value as number)}
          >
            {instances.map((instance) => (
              <MenuItem
                key={instance.model_instance_id}
                value={instance.model_instance_id}
              >
                {instance.forecast_model_name} · {instance.model_instance_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tabs value={tab} onChange={(_, next) => setTab(next)}>
          <Tab label="Prompts" value="prompts" />
          <Tab label="Executive access" value="config" />
          <Tab label="What-If" value="whatif" />
          <Tab label="Tornado" value="tornado" />
        </Tabs>

        {instanceId === '' ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
            Select a model instance to manage its executive configuration.
          </Typography>
        ) : tab === 'prompts' ? (
          <PromptsTab instanceId={instanceId} />
        ) : tab === 'config' ? (
          <ConfigTab instanceId={instanceId} />
        ) : tab === 'whatif' ? (
          <WhatIfTab instanceId={instanceId} />
        ) : (
          <PromptsTab
            instanceId={instanceId}
            tool={TORNADO_TOOL}
            headerCopy="Publish new analyses from a generated Tornado report in the Reports tab."
            rowCaption={tornadoDefaultPairCaption}
          />
        )}
      </Stack>
    </PopupDialog>
  );
};

// ---------------------------------------------------------------------------
// Prompts tab
//
// Also reused (via the `tool` filter) as the Tornado tab: the management
// affordances — edit / enable / reorder / delete / health — are identical
// for every ExecutivePrompt type; only the listing filter, the header copy
// (where new items come from), and an optional per-row caption differ.
// ---------------------------------------------------------------------------

/** "Default: Base vs Upside" caption for a tornado row — the frozen default
 * pair that seeds the executive's Baseline / Compare-to dropdowns. */
const tornadoDefaultPairCaption = (prompt: LibraryPrompt): string | null => {
  try {
    const params = JSON.parse(prompt.params) as TornadoPromptParams;
    if (!params.default_scenario_a_name || !params.default_scenario_b_name) {
      return null;
    }
    return `Default: ${params.default_scenario_a_name} vs ${params.default_scenario_b_name}`;
  } catch {
    return null;
  }
};

const PromptsTab: React.FC<{
  instanceId: number;
  tool?: string;
  headerCopy?: string;
  rowCaption?: (prompt: LibraryPrompt) => string | null;
}> = ({
  instanceId,
  tool,
  headerCopy = 'Save new prompts from a completed Chart Visualization result card in the chat.',
  rowCaption,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();

  const [prompts, setPrompts] = useState<LibraryPrompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [health, setHealth] = useState<Map<number, LibraryHealthResult>>(
    new Map()
  );
  const [healthRunning, setHealthRunning] = useState(false);
  const [editing, setEditing] = useState<LibraryPrompt | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    listLibraryPrompts(instanceId, tool)
      .then((resp) => setPrompts(resp.prompts ?? []))
      .catch(() => setError('Failed to load the prompt library.'))
      .finally(() => setLoading(false));
  }, [instanceId, tool]);

  useEffect(() => {
    setHealth(new Map());
    reload();
  }, [reload]);

  const grouped = useMemo(() => {
    const byCategory = new Map<string, LibraryPrompt[]>();
    prompts.forEach((p) => {
      const existing = byCategory.get(p.category);
      if (existing) existing.push(p);
      else byCategory.set(p.category, [p]);
    });
    return Array.from(byCategory.entries());
  }, [prompts]);

  const runHealthCheck = async () => {
    setHealthRunning(true);
    try {
      const resp = await libraryHealthCheck(instanceId);
      setHealth(new Map(resp.results.map((r) => [r.prompt_id, r])));
    } catch {
      enqueueSnackbar('Health check failed to run.', { variant: 'error' });
    } finally {
      setHealthRunning(false);
    }
  };

  const toggleEnabled = async (prompt: LibraryPrompt) => {
    try {
      await patchLibraryPrompt(prompt.id, {
        enabled: prompt.enabled === 1 ? 0 : 1,
      });
      reload();
    } catch {
      enqueueSnackbar('Failed to update the prompt.', { variant: 'error' });
    }
  };

  const move = async (
    categoryItems: LibraryPrompt[],
    index: number,
    delta: -1 | 1
  ) => {
    const target = index + delta;
    if (target < 0 || target >= categoryItems.length) return;
    // Reassign sequential sort orders for the whole category so moves
    // behave even when legacy rows share the same SortOrder.
    const next = [...categoryItems];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    try {
      await Promise.all(
        next
          .map((p, i) => ({ p, i }))
          .filter(({ p, i }) => p.sort_order !== i)
          .map(({ p, i }) => patchLibraryPrompt(p.id, { sort_order: i }))
      );
      reload();
    } catch {
      enqueueSnackbar('Failed to reorder prompts.', { variant: 'error' });
    }
  };

  const remove = (prompt: LibraryPrompt) => {
    confirm({
      description: `Delete "${stripPromptMarkdown(prompt.title)}" from the executive library? Executives will no longer see it.`,
    })
      .then(async () => {
        try {
          await deleteLibraryPrompt(prompt.id);
          reload();
        } catch {
          enqueueSnackbar('Failed to delete the prompt.', {
            variant: 'error',
          });
        }
      })
      .catch(() => undefined);
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {headerCopy}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={runHealthCheck}
          disabled={healthRunning || prompts.length === 0}
          sx={{ textTransform: 'none' }}
        >
          {healthRunning ? 'Checking…' : 'Run health check'}
        </Button>
      </Stack>

      {loading && <CircularProgress size={20} />}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && prompts.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No prompts saved for this instance yet.
        </Typography>
      )}

      {grouped.map(([category, items]) => (
        <Box key={category} sx={{ mb: 2 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}
          >
            {category}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {items.map((prompt, index) => {
            const rowHealth = health.get(prompt.id);
            return (
              <Stack
                key={prompt.id}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ py: 0.75 }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                    {renderPromptTitle(prompt.title)}
                  </Typography>
                  {prompt.subtitle && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      display="block"
                    >
                      {prompt.subtitle}
                    </Typography>
                  )}
                  {rowCaption?.(prompt) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      display="block"
                    >
                      {rowCaption(prompt)}
                    </Typography>
                  )}
                </Box>
                {rowHealth && (
                  <Tooltip
                    title={
                      rowHealth.healthy
                        ? 'Bindings resolve against the current model instance.'
                        : rowHealth.errors.join(' · ')
                    }
                  >
                    <Chip
                      size="small"
                      label={rowHealth.healthy ? 'Healthy' : 'Broken'}
                      color={rowHealth.healthy ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </Tooltip>
                )}
                <Tooltip title="Move up">
                  <span>
                    <IconButton
                      size="small"
                      disabled={index === 0}
                      onClick={() => move(items, index, -1)}
                    >
                      <ArrowUpwardIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down">
                  <span>
                    <IconButton
                      size="small"
                      disabled={index === items.length - 1}
                      onClick={() => move(items, index, 1)}
                    >
                      <ArrowDownwardIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => setEditing(prompt)}>
                    <EditOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    prompt.enabled === 1 ? 'Visible to executives' : 'Hidden'
                  }
                >
                  <Switch
                    size="small"
                    checked={prompt.enabled === 1}
                    onChange={() => toggleEnabled(prompt)}
                  />
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => remove(prompt)}>
                    <DeleteOutlineIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Stack>
            );
          })}
        </Box>
      ))}

      <EditPromptDialog
        prompt={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          reload();
        }}
      />
    </Box>
  );
};

const EditPromptDialog: React.FC<{
  prompt: LibraryPrompt | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ prompt, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (prompt) {
      setTitle(prompt.title);
      setSubtitle(prompt.subtitle ?? '');
      setCategory(prompt.category);
      setSaving(false);
    }
  }, [prompt]);

  const handleSave = async () => {
    if (!prompt) return;
    setSaving(true);
    try {
      await patchLibraryPrompt(prompt.id, {
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        category: category.trim(),
      });
      onSaved();
    } catch {
      setSaving(false);
      enqueueSnackbar('Failed to update the prompt.', { variant: 'error' });
    }
  };

  return (
    <PopupDialog
      open={prompt !== null}
      close={onClose}
      title="Edit Prompt"
      maxWidth="sm"
      primaryButtons={[
        {
          id: 'save',
          label: 'Save',
          onClick: handleSave,
          disabled: !title.trim() || !category.trim() || saving,
          loading: saving,
          variant: 'contained',
        },
      ]}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField
          label="Executive-facing question"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          inputProps={{ maxLength: 255 }}
          helperText={PROMPT_TITLE_BOLD_HELP}
        />
        <TextField
          label="Subtitle (optional)"
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          fullWidth
          inputProps={{ maxLength: 255 }}
        />
        <TextField
          label="Category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          fullWidth
          required
          inputProps={{ maxLength: 100 }}
        />
      </Stack>
    </PopupDialog>
  );
};

// ---------------------------------------------------------------------------
// Executive access (config) tab
// ---------------------------------------------------------------------------

const ConfigTab: React.FC<{ instanceId: number }> = ({ instanceId }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [config, setConfig] = useState<ExecutiveConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [subheader, setSubheader] = useState('');
  // Exec AI year window (C1): kept as strings so an empty field reads as
  // "unset -> use the global default" (saved as null), distinct from 0.
  const [yearsBefore, setYearsBefore] = useState('');
  const [yearsAfter, setYearsAfter] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setConfig(null);
    setError(null);
    getExecutiveConfig(instanceId)
      .then((resp) => {
        if (cancelled) return;
        setConfig(resp);
        setEnabled(resp.enabled);
        setSubheader(resp.subheader ?? '');
        setYearsBefore(
          resp.exec_ai_years_before == null
            ? ''
            : String(resp.exec_ai_years_before)
        );
        setYearsAfter(
          resp.exec_ai_years_after == null
            ? ''
            : String(resp.exec_ai_years_after)
        );
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load the executive config.');
      });
    return () => {
      cancelled = true;
    };
  }, [instanceId]);

  // Parse a year-window field: empty -> null (global default); otherwise a
  // non-negative integer. Returns undefined for an invalid entry so the save
  // can block on it.
  const parseYearField = (raw: string): number | null | undefined => {
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    if (!Number.isInteger(n) || n < 0) return undefined;
    return n;
  };

  const handleSave = async () => {
    const before = parseYearField(yearsBefore);
    const after = parseYearField(yearsAfter);
    if (before === undefined || after === undefined) {
      enqueueSnackbar(
        'Year window values must be whole numbers of 0 or more (leave blank for the default).',
        { variant: 'error' }
      );
      return;
    }
    setSaving(true);
    try {
      await putExecutiveConfig(instanceId, {
        enabled,
        subheader: subheader.trim() || null,
        exec_ai_years_before: before,
        exec_ai_years_after: after,
      });
      enqueueSnackbar('Executive config saved.', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(
        e instanceof Error ? e.message : 'Failed to save the config.',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!config) return <CircularProgress size={20} />;

  return (
    <Stack spacing={2} sx={{ maxWidth: 640 }}>
      <FormControlLabel
        control={
          <Switch checked={enabled} onChange={() => setEnabled((x) => !x)} />
        }
        label="Enabled for executives (shows as a brand card in the Executive View)"
      />
      <TextField
        label="Brand card subheader"
        placeholder="e.g. the indication — Metastatic melanoma"
        value={subheader}
        onChange={(e) => setSubheader(e.target.value)}
        inputProps={{ maxLength: 255 }}
        size="small"
      />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Chart & download year window
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Limits the years shown in Executive time-series charts and Excel
          downloads to a window around the current year. Leave blank to show all
          available years.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Years before"
            type="number"
            value={yearsBefore}
            onChange={(e) => setYearsBefore(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            size="small"
            sx={{ width: 140 }}
          />
          <TextField
            label="Years after"
            type="number"
            value={yearsAfter}
            onChange={(e) => setYearsAfter(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            size="small"
            sx={{ width: 140 }}
          />
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Scenarios executives will see
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Executives can select from all AI-enabled (tagged) scenarios on this
          forecast model — there is no separate executive whitelist. Tag or
          untag scenarios in the Scenario Manager to change this list.
        </Typography>
        <Box sx={{ mt: 1 }}>
          {config.scenarios.length === 0 && (
            <Alert severity="warning" sx={{ py: 0 }}>
              No tagged scenarios — an enabled brand will not appear in the
              Executive View until at least one scenario is tagged.
            </Alert>
          )}
          {config.scenarios.map((s) => (
            <Typography
              key={s.scenario_id}
              variant="body2"
              noWrap
              sx={{ py: 0.25 }}
            >
              {s.name}
              {s.tag ? ` · ${s.tag}` : ''}
            </Typography>
          ))}
        </Box>
      </Box>

      <Box>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: 'none' }}
        >
          {saving ? 'Saving…' : 'Save executive config'}
        </Button>
      </Box>
    </Stack>
  );
};

export default LibraryManagerModal;
